import { useEffect, useState, useCallback } from "react";
import { getScaledCoords } from "../utils/coordinateUtils";
import { findClosestPoint } from "../utils/canvasUtils";
import {
  DRAW_MODES,
  ZONE_TYPES,
  LINE_CROSSING_STEPS,
} from "../utils/constants";
import "./CanvasContainer.css";

export default function CanvasContainer({
  canvasRef,
  containerRef,
  scaleFactor,
  isImageLoaded,
  cursor,
  drawMode,
  editMode,
  points,
  masterPoints,
  selectedPointIndex,
  selectedPolygonIndex,
  constrainAngles,
  currentZoneType,
  lineCrossingStep,
  directionLine,
  crossingLine,
  getCurrentColor,
  getConstrainedCoords,
  onAddPoint,
  onMovePoint,
  onSelectPoint,
  onLoadImage,
  onZoom,
  onDrawTemporary,
  onDrawLineCrossingProgress,
  onClosePolygon,
  onCoordinatesChange,
  setCursorStyle,
}) {
  const [isDragging, setIsDragging] = useState(false);

  // Debug log to check if dropZone should be visible
  useEffect(() => {
    console.log('CanvasContainer - isImageLoaded:', isImageLoaded);
    console.log('DropZone should be visible:', !isImageLoaded);
  }, [isImageLoaded]);

  // Handle mouse events
  const handleMouseDown = useCallback(
    (e) => {
      if (!isImageLoaded) return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const [x, y] = getScaledCoords(e, canvas, scaleFactor);
      const roundedX = Math.round(x);
      const roundedY = Math.round(y);

      if (editMode) {
        const { point, polygonIndex, pointIndex } = findClosestPoint(
          roundedX,
          roundedY,
          masterPoints,
          scaleFactor,
        );

        if (point) {
          onSelectPoint(polygonIndex, pointIndex);
          setIsDragging(true);
          setCursorStyle("grabbing");
        }
      } else {
        // Drawing mode
        setCursorStyle("crosshair");

        let finalCoords = [roundedX, roundedY];

        if (constrainAngles && points.length > 0) {
          finalCoords = getConstrainedCoords(roundedX, roundedY);
        }

        onAddPoint(finalCoords[0], finalCoords[1]);
      }
    },
    [
      isImageLoaded,
      canvasRef,
      scaleFactor,
      editMode,
      masterPoints,
      constrainAngles,
      points,
      getConstrainedCoords,
      onSelectPoint,
      onAddPoint,
      setCursorStyle,
    ],
  );

  const handleMouseUp = useCallback(() => {
    if (editMode && isDragging) {
      setIsDragging(false);
      setCursorStyle("move");
    }
  }, [editMode, isDragging, setCursorStyle]);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isImageLoaded) return;

      const canvas = canvasRef.current;

      if (!canvas) return;

      const [x, y] = getScaledCoords(e, canvas, scaleFactor);
      let roundedX = Math.round(x);
      let roundedY = Math.round(y);

      // Apply angle constraints if needed
      if (constrainAngles && points.length > 0 && !editMode) {
        [roundedX, roundedY] = getConstrainedCoords(roundedX, roundedY);
      }

      // Check bounds
      if (roundedX < 0 || roundedY < 0) {
        onCoordinatesChange({ x: "", y: "" });

        return;
      }

      onCoordinatesChange({ x: roundedX, y: roundedY });

      if (editMode && isDragging && selectedPointIndex !== -1) {
        // Move selected point
        onMovePoint(
          selectedPolygonIndex,
          selectedPointIndex,
          roundedX,
          roundedY,
        );
      } else if (!editMode && cursor === "crosshair") {
        // Update preview
        if (drawMode === DRAW_MODES.LINE_CROSSING) {
          // Handle line crossing preview
          let previewPoints = [];
          const previewColor = getCurrentColor();

          if (lineCrossingStep === LINE_CROSSING_STEPS.DIRECTION) {
            if (directionLine.length === 1) {
              previewPoints = [...directionLine, [roundedX, roundedY]];
            }
          } else if (lineCrossingStep === LINE_CROSSING_STEPS.CROSSING) {
            if (crossingLine.length === 1) {
              previewPoints = [...crossingLine, [roundedX, roundedY]];
            }
          }

          onDrawTemporary(
            previewPoints,
            previewColor,
            roundedX,
            roundedY,
            DRAW_MODES.LINE,
          );
        } else {
          onDrawTemporary(
            points,
            getCurrentColor(),
            roundedX,
            roundedY,
            drawMode,
          );
        }

        // Also draw line crossing work in progress if in line crossing mode
        if (
          drawMode === DRAW_MODES.LINE_CROSSING &&
          (directionLine.length > 0 || crossingLine.length > 0)
        ) {
          setTimeout(() => {
            onDrawLineCrossingProgress(
              directionLine,
              crossingLine,
              lineCrossingStep,
            );
          }, 0);
        }
      }
    },
    [
      isImageLoaded,
      canvasRef,
      scaleFactor,
      constrainAngles,
      points,
      editMode,
      isDragging,
      selectedPointIndex,
      selectedPolygonIndex,
      cursor,
      drawMode,
      lineCrossingStep,
      directionLine,
      crossingLine,
      getConstrainedCoords,
      onCoordinatesChange,
      onMovePoint,
      onDrawTemporary,
      onDrawLineCrossingProgress,
      getCurrentColor,
    ],
  );

  const handleMouseLeave = useCallback(() => {
    onCoordinatesChange({ x: "", y: "" });
  }, [onCoordinatesChange]);

  const handleWheel = useCallback(
    (e) => {
      e.preventDefault();

      const delta = Math.sign(e.deltaY);

      onZoom(delta);
    },
    [onZoom],
  );

  const handleDragOver = useCallback((e) => {
    console.log('DragOver event triggered');
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDragEnter = useCallback((e) => {
    console.log('DragEnter event triggered');
    e.preventDefault();
    const dropZone = containerRef.current?.querySelector('.canvas-drop-zone');
    console.log('DropZone element found:', !!dropZone);
    if (dropZone) {
      dropZone.classList.add('drag-over');
      console.log('Added dragOver class');
    }
  }, [containerRef]);

  const handleDragLeave = useCallback((e) => {
    console.log('DragLeave event triggered');
    e.preventDefault();
    // Only remove dragOver class if we're leaving the dropZone/container entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    // Check if mouse is still within the dropZone bounds
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      console.log('Mouse left bounds, removing dragOver class');
      const dropZone = containerRef.current?.querySelector('.canvas-drop-zone');
      if (dropZone) {
        dropZone.classList.remove('drag-over');
      }
    }
  }, [containerRef]);

  const handleDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();

      console.log('Drop event triggered');

      // Remove drag over styling
      const dropZone = containerRef.current?.querySelector('.canvas-drop-zone');
      if (dropZone) {
        dropZone.classList.remove('drag-over');
      }

      const files = e.dataTransfer.files;

      console.log('Files dropped:', files.length);

      if (files.length === 0) {
        console.error('No files dropped');

        return;
      }

      const file = files[0];
      console.log('File details:', file.name, file.type, file.size);

      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Please drop a valid image file (PNG, JPEG, JPG, or WebP)');

        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        alert('File size must be less than 10MB');

        return;
      }

      try {
        console.log('Loading image...');
        const success = await onLoadImage(file);
        if (!success) {
          console.error('Failed to load image');
        } else {
          console.log('Image loaded successfully');
        }
      } catch (error) {
        console.error('Error loading image:', error);
        alert('Failed to load image. Please try again.');
      }
    },
    [onLoadImage, containerRef],
  );

  // Set cursor based on mode and state
  useEffect(() => {
    if (!isImageLoaded) {
      setCursorStyle("default");

      return;
    }

    if (editMode) {
      setCursorStyle(isDragging ? "grabbing" : "move");
    } else {
      setCursorStyle("crosshair");
    }
  }, [editMode, isDragging, isImageLoaded, setCursorStyle]);

  // Prevent default drag and drop behavior on the entire document
  useEffect(() => {
    console.log('Setting up document-level drag and drop prevention');
    const preventDefaults = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Add listeners to prevent default behavior
    const events = ['dragenter', 'dragover', 'dragleave', 'drop'];
    events.forEach(eventName => {
      document.addEventListener(eventName, preventDefaults, false);
      document.body.addEventListener(eventName, preventDefaults, false);
    });
    console.log('Document drag and drop listeners added');

    return () => {
      // Clean up listeners
      events.forEach(eventName => {
        document.removeEventListener(eventName, preventDefaults, false);
        document.body.removeEventListener(eventName, preventDefaults, false);
      });
      console.log('Document drag and drop listeners removed');
    };
  }, []);

  // Handle keyboard events for polygon closing
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === "Enter" &&
        points.length > 2 &&
        drawMode === DRAW_MODES.POLYGON
      ) {
        onClosePolygon();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [points.length, drawMode, onClosePolygon]);

  return (
    <div ref={containerRef} className="canvas-container">
      <canvas
        ref={canvasRef}
        className="canvas-main"
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ cursor }}
      />
      {!isImageLoaded && (
        <div
          className="canvas-drop-zone"
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onMouseEnter={() => console.log('Mouse entered dropZone')}
          onMouseLeave={() => console.log('Mouse left dropZone')}
        >
          <div className="canvas-drop-zone-content">
            <i className="fa-solid fa-cloud-arrow-up"></i>
            <p>Drop an image here to start drawing DeepStream zones</p>
            <p className="canvas-drop-zone-formats">
              Supported formats: PNG, JPEG, JPG, WebP
            </p>
          </div>
        </div>
      )}

      {/* Line Crossing Step Indicator */}
      {isImageLoaded &&
        currentZoneType === ZONE_TYPES.LINE_CROSSING &&
        !editMode && (
          <div className="canvas-line-crossing-indicator">
            <div className="canvas-step-indicator">
              <span className="canvas-step-label">
                Step{" "}
                {lineCrossingStep === LINE_CROSSING_STEPS.DIRECTION ? "1" : "2"}{" "}
                of 2:
              </span>
              <span className="canvas-step-description">
                {lineCrossingStep === LINE_CROSSING_STEPS.DIRECTION
                  ? "Draw direction line (expected movement)"
                  : "Draw crossing line (detection line)"}
              </span>
            </div>
          </div>
        )}
    </div>
  );
}
